import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Plus,
    Smartphone,
    ArrowRight,
    ExternalLink,
    HelpCircle,
    Train,
    ShieldCheck,
    Search,
    ChevronRight,
    Wifi,
    CreditCard as CardIcon,
    MapPin
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import MockPayment from '../components/MockPayment';
import AdCarousel from '../components/AdCarousel';
import { trainRoutes, allSponsoredAds } from '../data/mockData';

const WalletView = () => {
    const { user, updateBalance } = useApp();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab') || 'wallet';

    const [activeTab, setActiveTab] = useState(initialTab);
    const [showPayment, setShowPayment] = useState(false);
    const [payAmount, setPayAmount] = useState(0);

    const openMockLink = (url) => {
        alert(`Opening ${url} (Mock Integration)`);
    };

    const getAdsForTab = () => {
        if (activeTab === 'wallet') return allSponsoredAds.filter(a => a.category === 'Logistics' || a.category === 'Drivers');
        if (activeTab === 'train') return allSponsoredAds.filter(a => a.category === 'Trains' || a.category === 'Taxi');
        return allSponsoredAds.slice(0, 3);
    };

    const displayAds = getAdsForTab();

    return (
        <div className="wallet-view animate-fade">
            <div className="wallet-tabs">
                <button className={activeTab === 'wallet' ? 'active' : ''} onClick={() => setActiveTab('wallet')}>Wallet</button>
                <button className={activeTab === 'train' ? 'active' : ''} onClick={() => setActiveTab('train')}>Train</button>
                <button className={activeTab === 'esim' ? 'active' : ''} onClick={() => setActiveTab('esim')}>eSIM</button>
            </div>

            <AdCarousel providers={displayAds.length > 0 ? displayAds : allSponsoredAds.slice(0, 3)} title="Sponsored Places" />

            {activeTab === 'wallet' && (
                <div className="wallet-content">
                    <div className="balance-hero">
                        <span className="label">Available NGN</span>
                        <h1>{user.balance.toLocaleString('en-NG', { style: 'currency', currency: 'NGN' })}</h1>
                        <p className="disclaimer">Demo Environment: Partner Mocks</p>
                    </div>

                    <div className="connect-section">
                        <div className="sec-title">
                            <h3>Connect Local Wallet</h3>
                            <HelpCircle size={16} />
                        </div>
                        <p className="hint-text">Connect Opay or Palmpay to use local NGN banking features.</p>

                        <div className="connection-cards">
                            <div className="conn-card card">
                                <div className="conn-brand">
                                    <div className="brand-logo opay">O</div>
                                    <div>
                                        <h4>Opay Wallet</h4>
                                        <span>Pay merchants & transfer</span>
                                    </div>
                                </div>
                                <button className="link-btn" onClick={() => openMockLink('opay://open')}>Open App <ExternalLink size={14} /></button>
                            </div>

                            <div className="conn-card card">
                                <div className="conn-brand">
                                    <div className="brand-logo palmpay">P</div>
                                    <div>
                                        <h4>Palmpay</h4>
                                        <span>Fast transfers & bills</span>
                                    </div>
                                </div>
                                <button className="link-btn" onClick={() => openMockLink('palmpay://open')}>Open App <ExternalLink size={14} /></button>
                            </div>
                        </div>
                    </div>

                    <div className="cowry-section">
                        <h3>Cowry Transit Card</h3>
                        <div className="cowry-card-view card">
                            <div className="card-vis">
                                <div className="card-chip"></div>
                                <div className="card-brand">COWRY</div>
                                <div className="card-num">**** **** **** 9012</div>
                            </div>
                            <div className="card-acts">
                                <div className="act-info">
                                    <span className="bal-l">Card Balance</span>
                                    <span className="bal-v">₦4,200</span>
                                </div>
                                <button className="primary-btn sm" onClick={() => { setPayAmount(2000); setShowPayment(true); }}>Top Up</button>
                            </div>
                        </div>
                        <div className="cowry-order card" onClick={() => openMockLink('cowry://order')}>
                            <div className="order-info">
                                <h4>Need a new Card?</h4>
                                <p>Order a physical Cowry card to your hotel.</p>
                            </div>
                            <ChevronRight color="var(--primary-green)" />
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'train' && (
                <div className="train-content">
                    <div className="train-search card">
                        <div className="search-field">
                            <Search size={18} />
                            <input type="text" placeholder="Lagos (Mobolaji Johnson)" readOnly />
                        </div>
                        <div className="search-field">
                            <MapPin size={18} />
                            <input type="text" placeholder="To: Ibadan" />
                        </div>
                        <button className="primary-btn full-w">Find Routes</button>
                    </div>

                    <div className="results-list">
                        <h3>Available Routes (Mobolaji Johnson Station)</h3>
                        {trainRoutes.map(route => (
                            <div key={route.id} className="route-card card">
                                <div className="route-header">
                                    <Train size={24} color="var(--primary-green)" />
                                    <div className="time-info">
                                        <h4>{route.time}</h4>
                                        <p>{route.from} → {route.to}</p>
                                    </div>
                                </div>
                                <div className="route-footer">
                                    <span className="price">₦{route.price.toLocaleString()}</span>
                                    <button className="secondary-btn sm" onClick={() => { setPayAmount(route.price); setShowPayment(true); }}>Book Seat</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'esim' && (
                <div className="esim-content">
                    <div className="esim-hero">
                        <Wifi size={48} color="var(--primary-green)" />
                        <h2>Lagos Connectivity</h2>
                        <p>Get a local eSIM or Physical SIM delivered to your arrival airport or hotel.</p>
                    </div>

                    <div className="esim-packages">
                        <div className="pkg card">
                            <div className="pkg-title">
                                <h4>Starter Pack</h4>
                                <p>10GB + 200 Mins Local</p>
                            </div>
                            <div className="pkg-price">
                                <span>₦5,000</span>
                                <button className="primary-btn sm" onClick={() => { setPayAmount(5000); setShowPayment(true); }}>Order</button>
                            </div>
                        </div>
                        <div className="pkg card">
                            <div className="pkg-title">
                                <h4>Tourist Pro</h4>
                                <p>50GB Unlimited Socials</p>
                            </div>
                            <div className="pkg-price">
                                <span>₦12,000</span>
                                <button className="primary-btn sm" onClick={() => { setPayAmount(12000); setShowPayment(true); }}>Order</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <MockPayment
                isOpen={showPayment}
                onClose={() => setShowPayment(false)}
                amount={payAmount}
                title="Payment Confirmation"
                onSuccess={() => alert("Transaction Successful! Check your email for details.")}
            />

            <style dangerouslySetInnerHTML={{
                __html: `
        .wallet-tabs { display: flex; gap: 8px; margin-bottom: 24px; padding: 4px; background: #f0f0f0; border-radius: var(--radius-md); }
        .wallet-tabs button { flex: 1; padding: 10px; border-radius: 8px; font-weight: 700; color: var(--text-light); background: transparent; }
        .wallet-tabs button.active { background: white; color: var(--primary-green); box-shadow: 0 2px 6px rgba(0,0,0,0.05); }

        .balance-hero { background: var(--primary-green); color: white; border-radius: var(--radius-lg); padding: 30px; text-align: center; margin-bottom: 30px; }
        .balance-hero .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8; }
        .balance-hero h1 { font-size: 36px; margin: 4px 0; color: white; }
        .balance-hero .disclaimer { font-size: 11px; opacity: 0.6; margin-top: 10px; }

        .sec-title { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .sec-title h3 { font-size: 16px; margin: 0; }
        .hint-text { font-size: 12px; color: var(--text-light); margin-bottom: 16px; }

        .connection-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 30px; }
        .conn-card { flex-direction: column; gap: 16px; padding: 16px; border: 1px solid var(--border-gray); }
        .conn-brand { display: flex; gap: 10px; align-items: flex-start; }
        .brand-logo { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: 14px; }
        .brand-logo.opay { background: #00d3a5; }
        .brand-logo.palmpay { background: #5a31f4; }
        .conn-brand h4 { font-size: 14px; margin: 0; }
        .conn-brand span { font-size: 10px; color: var(--text-light); line-height: 1.2; display: block; }
        .link-btn { background: var(--light-green); color: var(--primary-green); padding: 8px; border-radius: 6px; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 6px; }

        .cowry-card-view { background: linear-gradient(135deg, #001f3f 0%, #0074d9 100%); color: white; border: none; padding: 20px; margin-bottom: 12px; }
        .card-vis { height: 100px; display: flex; flex-direction: column; justify-content: space-between; margin-bottom: 20px; }
        .card-chip { width: 40px; height: 30px; background: #d4af37; border-radius: 4px; position: relative; }
        .card-brand { font-weight: 800; letter-spacing: 2px; text-align: right; }
        .card-num { font-family: monospace; font-size: 16px; letter-spacing: 2px; }
        
        .card-acts { display: flex; justify-content: space-between; align-items: flex-end; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 16px; }
        .bal-l { font-size: 11px; opacity: 0.8; display: block; }
        .bal-v { font-size: 18px; font-weight: 700; }

        .cowry-order { display: flex; align-items: center; justify-content: space-between; border-style: dashed; }
        .order-info h4 { font-size: 14px; margin: 0; }
        .order-info p { font-size: 11px; color: var(--text-light); margin: 0; }

        .train-search { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
        .search-field { display: flex; align-items: center; gap: 12px; border: 1px solid var(--border-gray); padding: 12px; border-radius: 8px; }
        .search-field input { border: none; outline: none; width: 100%; font-size: 14px; }
        
        .results-list h3 { font-size: 16px; margin-bottom: 16px; color: var(--text-light); }
        .route-card { margin-bottom: 12px; }
        .route-header { display: flex; gap: 16px; margin-bottom: 16px; }
        .time-info h4 { font-size: 18px; margin: 0; }
        .time-info p { font-size: 12px; color: var(--text-light); margin: 0; }
        .route-footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--border-gray); padding-top: 12px; }
        .price { font-weight: 800; color: var(--primary-green); }

        .esim-hero { text-align: center; margin: 20px 0 40px; }
        .esim-hero p { font-size: 14px; color: var(--text-light); margin-top: 8px; padding: 0 40px; }
        .esim-packages .pkg { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
        .pkg-title h4 { font-size: 16px; margin: 0; }
        .pkg-title p { font-size: 12px; color: var(--text-light); margin: 0; }
        .pkg-price { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
        .pkg-price span { font-weight: 800; }
      `}} />
        </div>
    );
};

export default WalletView;
